import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Download, Upload, FileText, TrendingUp, AlertTriangle, Save,
  BarChart3, Trash2, Printer, Scale, Bell,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { money, money2, calcCommission, effectiveRate, branchRateOf, daysLeft, calcAgentCommission } from "../lib/finance";
import { API } from "../lib/apiClient";
import { LostCustomersPanel } from "./LostCustomersPanel";
import { CompanyReport } from "./CompanyReport";
import { RequestSourceReport } from "./RequestSourceReport";
import { QrScanReport } from "./QrScanReport";
import { PageTitle, Empty } from "./common";

const YEAR = new Date().getFullYear();
const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
const MONTHS_FULL = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

export function Reports({ quotes = [], policies, customers, branches, settings, exportCsv, exportExcel, can = () => true, onPolicyUpdated }) {
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupMsg, setBackupMsg] = useState("");
  const [backupUrl, setBackupUrl] = useState("");
  const availableYears = useMemo(() => {
    const years = new Set();
    policies.forEach((p) => { if (p.start) { const y = new Date(p.start).getFullYear(); if (Number.isFinite(y)) years.add(y); } });
    years.add(YEAR);
    return Array.from(years).sort((a, b) => b - a);
  }, [policies]);
  const [filterYear, setFilterYear] = useState(YEAR);
  const [filterMonth, setFilterMonth] = useState(""); // "" means all months
  const [filterBranch, setFilterBranch] = useState(""); // "" means all branches
  const doExcelExport = () => {
    exportExcel({
      year: filterYear || null,
      month: filterMonth ? Number(filterMonth) : null,
      branch: filterBranch || null,
    });
  };
  const runBackup = async () => {
    setBackupBusy(true);
    setBackupMsg("");
    setBackupUrl("");
    try {
      const { data } = await axios.post(`${API}/backup/run`);
      if (data.emailSent) {
        setBackupMsg(`Yedek e-postanıza gönderildi · ${data.stats?.policies || 0} poliçe · bağlantı 7 gün geçerli`);
      } else {
        setBackupMsg(`Yedek oluşturuldu · e-posta şu an gönderilemedi (deneme limiti). Aşağıdaki bağlantıdan indirebilirsiniz.`);
        setBackupUrl(data.downloadUrl || "");
      }
    } catch (e) {
      setBackupMsg(`Gönderim başarısız: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setBackupBusy(false);
    }
  };

  const branchNames = useMemo(() => Array.from(new Set([...branches.map((b) => b.name), ...policies.map((p) => p.branch)])).filter(Boolean), [branches, policies]);
  const data = branchNames.map((branch) => {
    const rows = policies.filter((p) => p.branch === branch);
    const brut = rows.reduce((s, p) => s + Number(p.premium || 0), 0);
    const personal = rows.reduce((s, p) => s + calcCommission(p.premium, branchRateOf(branch, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
    return { branch, prim: brut, personal };
  }).filter((x) => x.prim);
  return (
    <>
      <PageTitle eyebrow={`YILLIK PERFORMANS · ${YEAR}`} title="Üretim raporları" description="Branş ve müşteri bazında üretiminizi yazdırın veya yedekleyin." />
      <div className="toolbar">
        <div className="report-note">Rapor dönemi: {YEAR} · Her Pazar sabahı e-posta ile otomatik yedek</div>
        <div className="toolbar-actions">
          {can("canRunBackup") && <button className="outline-button" data-testid="backup-now-button" onClick={runBackup} disabled={backupBusy}><Bell size={16} /> {backupBusy ? "Gönderiliyor…" : "Şimdi e-posta yedeği gönder"}</button>}
          <button className="outline-button" data-testid="print-report-button" onClick={() => window.print()}><FileText size={16} /> Yazdır / PDF</button>
          <button className="outline-button" data-testid="annual-report-button" onClick={() => openAnnualReportWindow(policies, branches, settings, new Date().getFullYear())}><FileText size={16} /> Muhasebe Raporu</button>
          <button className="outline-button" data-testid="report-csv-button" onClick={exportCsv}><Download size={16} /> CSV</button>
        </div>
      </div>

      <RequestSourceReport quotes={quotes} />
      <QrScanReport quotes={quotes} />
      <LostCustomersPanel policies={policies} onPolicyUpdated={onPolicyUpdated} />

      <section className="panel excel-export-panel" data-testid="excel-export-panel">
        <div className="panel-head">
          <div>
            <h2>Excel yedeği indir</h2>
            <p>Yıl ve ayı seçip filtreli Excel dosyası oluşturun. Ay seçmeden yalnız yıl seçerseniz tüm ayları getirir.</p>
          </div>
        </div>
        <div className="excel-export-controls">
          <label className="field">
            <span>Yıl</span>
            <select data-testid="export-filter-year" value={filterYear} onChange={(e) => setFilterYear(Number(e.target.value))}>
              {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Ay</span>
            <select data-testid="export-filter-month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
              <option value="">Tüm aylar</option>
              {MONTHS_FULL.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Branş</span>
            <select data-testid="export-filter-branch" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
              <option value="">Tüm branşlar</option>
              {branchNames.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <div className="excel-export-summary" data-testid="export-summary">
            {(() => {
              const count = policies.filter((p) => {
                if (!p.start) return false;
                const d = new Date(p.start);
                if (Number.isNaN(d.getTime())) return false;
                if (d.getFullYear() !== filterYear) return false;
                if (filterMonth && d.getMonth() + 1 !== Number(filterMonth)) return false;
                if (filterBranch && p.branch !== filterBranch) return false;
                return true;
              }).length;
              const parts = [];
              if (filterMonth) parts.push(MONTHS_FULL[Number(filterMonth) - 1]);
              parts.push(String(filterYear));
              if (filterBranch) parts.push(`${filterBranch} branşı`);
              else if (!filterMonth) parts.push("tüm yıl");
              return <><strong>{count}</strong> poliçe indirilecek · {parts.join(" · ")}</>;
            })()}
          </div>
          <button className="primary-button" data-testid="report-excel-button" onClick={doExcelExport}><Download size={16} /> Excel indir</button>
        </div>
      </section>
      {backupMsg && (
        <div className="backup-toast" data-testid="backup-toast">
          {backupMsg}
          {backupUrl && (
            <>
              {" "}
              <a href={backupUrl} target="_blank" rel="noreferrer" data-testid="backup-download-link" style={{ color: "#1a5fe0", fontWeight: 600, textDecoration: "underline" }}>Yedeği indir</a>
            </>
          )}
        </div>
      )}
      <section className="panel report-chart">
        <div className="panel-head"><div><h2>Branş bazında prim ve hak ediş</h2><p>Yıllık toplamlar · {YEAR}</p></div></div>
        <div className="chart-wrap tall">
          {data.length === 0 ? <Empty text="Henüz üretim verisi yok" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid vertical={false} stroke="#e8edf3" />
                <XAxis dataKey="branch" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="prim" name="Brüt prim" fill="#0f172a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="personal" name="Kişisel hak ediş" fill="#1a5fe0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <CompanyReport policies={policies} customers={customers} branches={branches} settings={settings} years={availableYears} />
      <section className="panel report-customers">
        <div className="panel-head"><div><h2>Müşteri üretim özeti</h2><p>Toplam prim ve tahmini hak ediş</p></div></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>MÜŞTERİ</th><th>POLİÇE</th><th>BRÜT PRİM</th><th>KİŞİSEL HAK EDİŞ</th><th>ACENTE PRİMİ</th></tr></thead>
            <tbody>
              {customers.map((c) => {
                const rows = policies.filter((p) => p.customer === c.name);
                const personal = rows.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
                const agentTotal = rows.reduce((s, p) => s + calcAgentCommission(p, customers, branches, settings).agent, 0);
                const custRate = Number(c?.commissionRate) || 0;
                return (
                  <tr key={c.id}>
                    <td>{c.name}{custRate > 0 && <small style={{ display: "block", color: "#64748b" }}>Komisyon %{custRate}</small>}</td>
                    <td>{rows.length}</td>
                    <td>{money(rows.reduce((s, p) => s + Number(p.premium || 0), 0))}</td>
                    <td className="commission">{money(personal)}</td>
                    <td>{agentTotal > 0 ? <span title={`%${custRate} müşteri komisyonu`}>{money(agentTotal)}</span> : <em className="dim">—</em>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------- Modals ----------------


function openAnnualReportWindow(policies, branches, settings, year) {
  const esc = (s) => (s == null ? "" : String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])));
  const fmt = (v) => Number(v || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 });
  const inYear = policies.filter((p) => (p.start || "").slice(0, 4) === String(year));
  const active = inYear.filter((p) => p.status !== "iptal");
  const cancelled = inYear.filter((p) => p.status === "iptal");
  const totalGross = active.reduce((s, p) => s + Number(p.premium || 0), 0);
  const totalNet = active.reduce((s, p) => s + Number(p.netPremium || p.premium || 0), 0);
  const totalRefund = cancelled.reduce((s, p) => s + Number(p.cancellationRefund || 0), 0);
  const totalComm = active.reduce((s, p) => {
    const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, null);
    return s + c.personal;
  }, 0);
  const byBranch = {};
  active.forEach((p) => {
    const b = p.branch || "Diğer";
    if (!byBranch[b]) byBranch[b] = { count: 0, gross: 0, comm: 0 };
    byBranch[b].count += 1;
    byBranch[b].gross += Number(p.premium || 0);
    const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, null);
    byBranch[b].comm += c.personal;
  });
  const byMonth = Array(12).fill(0).map(() => ({ count: 0, gross: 0, comm: 0 }));
  active.forEach((p) => {
    const m = Number((p.start || "").slice(5, 7)) - 1;
    if (m >= 0 && m < 12) {
      byMonth[m].count += 1;
      byMonth[m].gross += Number(p.premium || 0);
      const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, null);
      byMonth[m].comm += c.personal;
    }
  });
  const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${year} Muhasebe Raporu</title>
    <style>
      @page{size:A4;margin:14mm}
      body{font-family:system-ui,-apple-system,Segoe UI,Arial;color:#0f172a;margin:0;padding:24px}
      h1{font-size:24px;margin:0 0 4px;color:#1447b8}
      .sub{color:#64748b;font-size:13px;margin:0 0 22px}
      .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:22px}
      .kpi{border:1px solid #e2e8f0;border-radius:8px;padding:12px}.kpi small{color:#94a3b8;font-size:10px;letter-spacing:.6px;text-transform:uppercase}.kpi strong{display:block;margin-top:6px;font-size:15px}
      .highlight strong{color:#1a5fe0}
      h2{font-size:14px;margin:22px 0 10px;color:#0f172a;border-bottom:1px solid #cbd5e1;padding-bottom:6px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{text-align:left;color:#94a3b8;font-weight:700;font-size:9px;padding:8px;border-bottom:1px solid #cbd5e1;text-transform:uppercase;letter-spacing:.5px}
      td{padding:8px;border-bottom:1px solid #f1f5f9}
      tfoot td{font-weight:700;border-top:2px solid #1a5fe0;border-bottom:0;background:#eff6ff}
      .footer{margin-top:30px;font-size:10px;color:#94a3b8;text-align:center}
      @media print{.footer{page-break-inside:avoid}}
    </style></head><body>
    <h1>${year} Yılı Muhasebe Raporu</h1>
    <p class="sub">Dönem: 01.01.${year} – 31.12.${year} · Rapor Tarihi: ${new Date().toLocaleDateString("tr-TR")}</p>
    <div class="kpis">
      <div class="kpi"><small>Toplam Poliçe</small><strong>${active.length}</strong></div>
      <div class="kpi"><small>Toplam Brüt Prim</small><strong>${fmt(totalGross)}</strong></div>
      <div class="kpi"><small>Toplam Net Prim</small><strong>${fmt(totalNet)}</strong></div>
      <div class="kpi highlight"><small>Net Kişisel Kazanç</small><strong>${fmt(totalComm)}</strong></div>
    </div>
    <h2>Branş Dağılımı</h2>
    <table><thead><tr><th>Branş</th><th>Poliçe</th><th>Brüt Prim</th><th>Kişisel Hak Ediş</th></tr></thead>
    <tbody>${Object.entries(byBranch).sort((a, b) => b[1].gross - a[1].gross).map(([b, s]) => `<tr><td>${esc(b)}</td><td>${s.count}</td><td>${fmt(s.gross)}</td><td>${fmt(s.comm)}</td></tr>`).join("")}</tbody>
    <tfoot><tr><td>Toplam</td><td>${active.length}</td><td>${fmt(totalGross)}</td><td>${fmt(totalComm)}</td></tr></tfoot></table>
    <h2>Aylık Dağılım</h2>
    <table><thead><tr><th>Ay</th><th>Poliçe</th><th>Brüt Prim</th><th>Kişisel Hak Ediş</th></tr></thead>
    <tbody>${byMonth.map((s, i) => s.count ? `<tr><td>${months[i]}</td><td>${s.count}</td><td>${fmt(s.gross)}</td><td>${fmt(s.comm)}</td></tr>` : "").join("")}</tbody></table>
    ${cancelled.length ? `<h2>İptal / İade</h2>
    <table><thead><tr><th>Poliçe</th><th>Müşteri</th><th>İade Tutarı</th></tr></thead>
    <tbody>${cancelled.map((p) => `<tr><td>${esc(p.policeNo || "-")}</td><td>${esc(p.customer || "-")}</td><td>${fmt(p.cancellationRefund)}</td></tr>`).join("")}</tbody>
    <tfoot><tr><td colspan="2">Toplam İade</td><td>${fmt(totalRefund)}</td></tr></tfoot></table>` : ""}
    <p class="footer">SigortaCrm · Muhasebeye hazır özet rapor · ${year}</p>
    <script>window.onload=()=>setTimeout(()=>window.print(),400)</script>
    </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { window.alert("Tarayıcı pop-up engelliyor olabilir. Lütfen izin verin."); return; }
  w.document.open(); w.document.write(html); w.document.close();
}
